import type { RoomPasswordProof } from '@andor/contracts/rooms';

/**
 * Salt prefix of the proof derivation. It keeps the proof independent of the
 * media keys the cipher uses (`room-media-key.ts`, salted
 * `andor/room-media/v1/`) — so the server never learns anything that decrypts
 * traffic.
 */
const PROOF_SALT_PREFIX = 'andor/room-access/v1/';

/** Matches the cipher's own key-derivation cost, so the proof is no weaker. */
export const ROOM_PASSWORD_PROOF_ITERATIONS = 600_000;

// Derivation is deliberately slow (~0.2–1 s), and every join of a password
// room needs a proof: memoise per tab. In memory only — the password itself is
// already saved in RoomPasswordStore.
const proofs = new Map<string, Promise<RoomPasswordProof>>();

/**
 * One-way proof that we hold `password` for `roomId`, sent with the create and
 * join requests of a password room (`RoomPasswordProofSchema`):
 * `PBKDF2-HMAC-SHA256(password, "andor/room-access/v1/" + roomId, 600k, 256
 * bits)` as unpadded base64url. `password` must be the exact stored password
 * (no trimming or normalisation), so the proof matches exactly when the media
 * keys would; `roomId` the canonical id (see `normalizeRoomId`).
 * `iterations` is injectable for fast unit tests only.
 */
export function deriveRoomPasswordProof(
  password: string,
  roomId: string,
  iterations = ROOM_PASSWORD_PROOF_ITERATIONS
): Promise<RoomPasswordProof> {
  const key = JSON.stringify([roomId, password, iterations]);
  let proof = proofs.get(key);
  if (!proof) {
    proof = derive(password, roomId, iterations);
    proofs.set(key, proof);
    proof.catch(() => proofs.delete(key));
  }
  return proof;
}

async function derive(
  password: string,
  roomId: string,
  iterations: number
): Promise<RoomPasswordProof> {
  const encoder = new TextEncoder();
  const material = await crypto.subtle.importKey('raw', encoder.encode(password), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: encoder.encode(PROOF_SALT_PREFIX + roomId),
      iterations,
    },
    material,
    256
  );
  return { v: 1, verifier: toBase64Url(new Uint8Array(bits)) };
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
